import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { Datepicker, PinAction, SubmissionButtons } from '@components/common';
import Checkbox from '@components/common/Checkbox';
import RadioButton from '@components/common/RadioButton';
import Select from '@components/common/Select';
import SliderCheckbox from '@components/common/SliderCheckbox';

import { useQueryAllOperatorsOokla } from '@hooks/ookla/useQueryAllOperatorsOokla';
import { useQueryAllTechnologiesOokla } from '@hooks/ookla/useQueryAllTechnologiesOokla';
import { useQueryDatesOokla } from '@hooks/ookla/useQueryDatesOokla';
import { useQueryMeasurementNamesOokla } from '@hooks/ookla/useQueryMeasurementNamesOokla';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import {
	resetAppliedOoklaFilters,
	resetRightSideControls,
	setAppliedOoklaFilters,
	setColorAssetsBy,
	setPinnedFilter,
} from '@slices/map';

import { isEmpty, uuid } from '@utils/common';
import { endOfDayInUTC, startOfDayInUTC } from '@utils/kpi-utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { EControlsType, ESnackbarTypes, TEControlsType } from '@enums/common';
import { EColorAssetsBy } from '@enums/map';

import colors from '@constants/colors';
import { EDeviceTypes, EMeasurementTypes, EOoklaDisplayTypes, TOoklaDisplayTypes } from '@constants/ookla';
import { responseMessages } from '@constants/responseMessages';
import { getLegendInfo } from '@components/Map/LegendaOokla/constants';

import styles from './FiltersOokla.module.scss';

const FiltersOokla = () => {
	const dispatch = useAppDispatch();

	const {
		appliedOoklaFilters: {
			isOoklaInfo,
			networkOperator,
			measurementName,
			deviceType,
			measurementType,
			dateFrom,
			type,
			technology,
			serviceLevelGood,
			serviceLevelVariable,
			serviceLevelNoService,
		},
	} = useAppSelector(map);

	const isServiceLevelGood = serviceLevelGood !== undefined ? serviceLevelGood : true;
	const isServiceLevelVariable = serviceLevelVariable !== undefined ? serviceLevelVariable : true;
	const isServiceLevelNoService = serviceLevelNoService !== undefined ? serviceLevelNoService : true;

	const [isPinned, setIsPinned] = useState(false);
	const [isOoklaInfoLocalState, setIsOoklaInfoLocalState] = useState(isOoklaInfo);
	const [selectedNetworkOperator, setSelectedNetworkOperator] = useState<TSelectOption>(networkOperator || null);
	const [selectedMeasurementName, setSelectedMeasurementName] = useState<TSelectOption>(measurementName || null);
	const [selectedType, setSelectedType] = useState<string>(type || EOoklaDisplayTypes.colorRelatedAssets);
	const [selectedTechnology, setSelectedTechnology] = useState<string | undefined>(technology);
	const [selectedDeviceType, setSelectedDeviceType] = useState<string>(deviceType || EDeviceTypes.unknown);
	const [selectedMeasurementType, setSelectedMeasurementType] = useState<string>(
		measurementType || EMeasurementTypes.unknown,
	);
	const [selectedDate, setSelectedDate] = useState<Date | null>(dateFrom ? new Date(dateFrom) : null);
	const [isServiceLevelGoodChecked, seIsServiceLevelGoodChecked] = useState<boolean>(isServiceLevelGood);
	const [isServiceLevelVariableChecked, seIsServiceLevelVariableChecked] = useState<boolean>(isServiceLevelVariable);
	const [isServiceLevelNoServiceChecked, seIsServiceLevelNoServiceChecked] = useState<boolean>(
		isServiceLevelNoService,
	);

	const containerRef = useRef<HTMLDivElement>(null);

	const { allOperatorsOokla, isLoadingAllOperatorsOokla } = useQueryAllOperatorsOokla();
	const { allTechnologiesOokla } = useQueryAllTechnologiesOokla();
	const { measurementNamesOokla, isLoadingMeasurementNamesOokla } = useQueryMeasurementNamesOokla();
	const { datesOokla, isLoadingDatesOokla } = useQueryDatesOokla();

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.mapSettings : null);
	}, [isPinned, pinFilter]);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	const onOoklaInfoChange = useCallback(() => {
		setIsOoklaInfoLocalState(prevState => !prevState);
	}, []);

	const handleReset = useCallback(() => {
		dispatch(resetAppliedOoklaFilters());

		if (!isPinned) dispatch(resetRightSideControls());
	}, [dispatch, isPinned]);

	const handleApply = useCallback(async () => {
		try {
			if (selectedType === EOoklaDisplayTypes.colorRelatedAssets) {
				dispatch(setColorAssetsBy(EColorAssetsBy.none));
			}

			dispatch(setAppliedOoklaFilters({
				isOoklaInfo: !!isOoklaInfoLocalState,
				type: selectedType,
				technology: selectedTechnology,
				networkOperator: selectedNetworkOperator,
				measurementName: selectedMeasurementName,
				deviceType: selectedDeviceType,
				measurementType: selectedMeasurementType,
				dateFrom: selectedDate && startOfDayInUTC(selectedDate),
				dateTo: selectedDate && endOfDayInUTC(selectedDate),
				serviceLevelGood: isServiceLevelGoodChecked,
				serviceLevelVariable: isServiceLevelVariableChecked,
				serviceLevelNoService: isServiceLevelNoServiceChecked,
			}));

			if (!isPinned) dispatch(resetRightSideControls());
		} catch (error) {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		}
	}, [
		dispatch,
		isPinned,
		isOoklaInfoLocalState,
		selectedNetworkOperator,
		selectedMeasurementName,
		selectedDeviceType,
		selectedMeasurementType,
		selectedDate,
		selectedType,
		selectedTechnology,
		isServiceLevelGoodChecked,
		isServiceLevelVariableChecked,
		isServiceLevelNoServiceChecked,
	]);

	const onNetworkOperatorChange = useCallback((value: TSelectOption) => {
		setSelectedNetworkOperator(value);
	}, []);

	const onMeasurementNameChange = useCallback((value: TSelectOption) => {
		setSelectedMeasurementName(value);
	}, []);

	const onDeviceTypeChange = useCallback((value: string) => {
		setSelectedDeviceType(value);
	}, []);

	const onTypeChange = useCallback((value: TOoklaDisplayTypes) => {
		setSelectedType(value);
	}, []);

	const onTechnologyChange = useCallback((value: string) => {
		setSelectedTechnology(value);
	}, []);

	const onMeasurementTypeChange = useCallback((value: string) => {
		setSelectedMeasurementType(value);
	}, []);

	const onChangeDate = useCallback((value: number | null) => {
		setSelectedDate(value ? new Date(value) : null);
	}, []);

	const onChangeServiceLevelGood = useCallback(() => {
		seIsServiceLevelGoodChecked(prevState => !prevState);
	}, []);

	const onChangeServiceLevelVariable = useCallback(() => {
		seIsServiceLevelVariableChecked(prevState => !prevState);
	}, []);

	const onChangeServiceLevelNoService = useCallback(() => {
		seIsServiceLevelNoServiceChecked(prevState => !prevState);
	}, []);

	const legendInfo = selectedMeasurementName?.name && getLegendInfo(selectedMeasurementName.name);

	const {
		textServiceLevelGood,
		textServiceLevelVariable,
		textServiceLevelNoService,
	} = legendInfo || {};

	const isFilterByLevelsShown = isOoklaInfoLocalState
		&& selectedMeasurementName
		&& textServiceLevelGood
		&& textServiceLevelVariable
		&& textServiceLevelNoService;

	const isApplyDisabled = !selectedType
		|| !selectedDate
		|| !selectedNetworkOperator
		|| !selectedTechnology
		|| !selectedDeviceType
		|| !selectedMeasurementType
		|| !selectedMeasurementName;

	return (
		<div
			ref={containerRef}
			className={styles.controlItemContainer}
		>
			<div className={styles.header}>
				<div className={styles.title}>
					Ookla data
				</div>
				<div className={styles.pinIconContainer}>
					<PinAction
						isPinned={isPinned}
						pinTooltip='Pin'
						setIsPinned={setIsPinned}
						tooltipPlacement='left'
						unpinTooltip='Unpin'
					/>
				</div>
			</div>
			<div className={styles.ooklaInfoContainer}>
				<div>Ookla information</div>
				<SliderCheckbox
					checked={isOoklaInfoLocalState}
					id='ooklaInfo'
					onChange={onOoklaInfoChange}
				/>
			</div>
			<div className={styles.radioButtonsContainer}>
				<div className={styles.label}>
					Type
				</div>
				<div className={cn(styles.radioButtons, styles.flexDirectionColumn)}>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedType === EOoklaDisplayTypes.colorRelatedAssets}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Color related assets'
							name='typeOokla'
							onChange={onTypeChange}
							value={EOoklaDisplayTypes.colorRelatedAssets}
						/>
					</div>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedType === EOoklaDisplayTypes.showCoverage}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Show coverage'
							name='typeOokla'
							onChange={onTypeChange}
							value={EOoklaDisplayTypes.showCoverage}
						/>
					</div>
				</div>
			</div>
			<div className={styles.dateContainer}>
				<div className={styles.label}>
					Date
				</div>
				<div className={styles.datepicker}>
					<Datepicker
						key='dateOokla'
						disableInput
						disabled={!isOoklaInfoLocalState}
						handleChange={onChangeDate}
						includeDates={datesOokla}
						isFullWidth
						isLoading={isLoadingDatesOokla}
						isMobileDevice={false}
						selected={selectedDate}
					/>
				</div>
			</div>
			<div className={styles.selectContainer}>
				<div className={styles.label}>
					Network Operator
				</div>
				<div className={styles.select}>
					<Select
						disabled={!isOoklaInfoLocalState}
						handleChange={onNetworkOperatorChange}
						isLoading={isLoadingAllOperatorsOokla}
						isWithoutUseForms
						name='networkOperatorOokla'
						options={allOperatorsOokla}
						placeholder='Select network operator'
						value={selectedNetworkOperator}
					/>
				</div>
			</div>
			{!isEmpty(allTechnologiesOokla) && allTechnologiesOokla
				&& <div className={styles.radioButtonsContainer}>
					<div className={styles.label}>
						Technology
					</div>
					<div className={cn(styles.radioButtons, styles.flexDirectionColumn)}>
						{allTechnologiesOokla.map(technology => {
							if (!technology?.name) return;

							return (
								<div
									key={uuid()}
									className={styles.radioButton}
								>
									<RadioButton
										checked={selectedTechnology === technology.name}
										disabled={!isOoklaInfoLocalState}
										id={uuid()}
										label={technology.name}
										name='technologyOokla'
										onChange={onTechnologyChange}
										value={technology.name}
									/>
								</div>
							);
						})}
					</div>
				</div>
			}
			<div className={styles.radioButtonsContainer}>
				<div className={styles.label}>
					Device type
				</div>
				<div className={styles.radioButtons}>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedDeviceType === EDeviceTypes.android}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Android'
							name='deviceTypeOokla'
							onChange={onDeviceTypeChange}
							value={EDeviceTypes.android}
						/>
					</div>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedDeviceType === EDeviceTypes.ios}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='iOS'
							name='deviceTypeOokla'
							onChange={onDeviceTypeChange}
							value={EDeviceTypes.ios}
						/>
					</div>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedDeviceType === EDeviceTypes.unknown}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Unknown'
							name='deviceTypeOokla'
							onChange={onDeviceTypeChange}
							value={EDeviceTypes.unknown}
						/>
					</div>
				</div>
			</div>
			<div className={styles.radioButtonsContainer}>
				<div className={styles.label}>
					Measurement type
				</div>
				<div className={styles.radioButtons}>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedMeasurementType === EMeasurementTypes.download}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Download'
							name='measurementTypeOokla'
							onChange={onMeasurementTypeChange}
							value={EMeasurementTypes.download}
						/>
					</div>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedMeasurementType === EMeasurementTypes.upload}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Upload'
							name='measurementTypeOokla'
							onChange={onMeasurementTypeChange}
							value={EMeasurementTypes.upload}
						/>
					</div>
					<div className={styles.radioButton}>
						<RadioButton
							checked={selectedMeasurementType === EMeasurementTypes.unknown}
							disabled={!isOoklaInfoLocalState}
							id={uuid()}
							label='Unknown'
							name='measurementTypeOokla'
							onChange={onMeasurementTypeChange}
							value={EMeasurementTypes.unknown}
						/>
					</div>
				</div>
			</div>
			<div className={styles.selectContainer}>
				<div className={styles.label}>
					Measurement
				</div>
				<div className={styles.select}>
					<Select
						disabled={!isOoklaInfoLocalState}
						handleChange={onMeasurementNameChange}
						isLoading={isLoadingMeasurementNamesOokla}
						isWithoutUseForms
						name='measurementNameOokla'
						options={measurementNamesOokla}
						placeholder='Select measurement'
						value={selectedMeasurementName}
					/>
				</div>
			</div>
			{isFilterByLevelsShown
				&& <div className={styles.checkboxContainer}>
					<div className={styles.label}>
						Filter by levels
					</div>
					<div className={styles.checkboxGroup}>
						<div className={styles.checkbox}>
							<Checkbox
								checked={isServiceLevelGoodChecked}
								color={colors.serviceLevelGood}
								label={textServiceLevelGood}
								name='filterByLevels'
								onChange={onChangeServiceLevelGood}
							/>
						</div>
						<div className={styles.checkbox}>
							<Checkbox
								checked={isServiceLevelVariableChecked}
								color={colors.serviceLevelVariable}
								label={textServiceLevelVariable}
								name='filterByLevels'
								onChange={onChangeServiceLevelVariable}
							/>
						</div>
						<div className={styles.checkbox}>
							<Checkbox
								checked={isServiceLevelNoServiceChecked}
								color={colors.serviceLevelNoService}
								label={textServiceLevelNoService}
								name='filterByLevels'
								onChange={onChangeServiceLevelNoService}
							/>
						</div>
					</div>
				</div>
			}
			<SubmissionButtons
				disabledPositive={isApplyDisabled}
				negativeHandler={handleReset}
				negativeLabel='Reset all'
				positiveHandler={handleApply}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default memo(FiltersOokla);
